/**
 * Hero Carousel Component
 * 7-day carousel showing 8:30pm screenings with video backgrounds
 * Optionally includes a featured banner as slide 1
 * Controlled by the themed nights navigation bar
 */

import { useState, useEffect, useRef } from '@wordpress/element';

export default function HeroCarousel({ picks, onTrailerClick, fallbackFilm }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef(null);
    const activeIndexRef = useRef(0);
    const t = window.rialtoData?.translations || {};

    // Keep ref in sync with state to avoid stale closures in event listeners
    useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

    // Use picks if available, otherwise fallback
    const slides = picks && picks.length > 0 ? picks : (fallbackFilm ? [{ film: fallbackFilm, day_short: '' }] : []);

    // Check if the first slide is a featured banner (for index offset calculations)
    const hasFeature = slides.length > 0 && slides[0].is_featured;

    // Auto-cycle every 5 seconds (increased from 3 for readability)
    useEffect(() => {
        if (slides.length <= 1 || isPaused) return;

        intervalRef.current = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setActiveIndex((prev) => (prev + 1) % slides.length);
                setIsTransitioning(false);
            }, 500);
        }, 5000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [slides.length, isPaused]);

    // Listen for themed nights navigation clicks to control the carousel
    useEffect(() => {
        const handleNightClick = (e) => {
            const slideIndex = parseInt(e.detail?.slideIndex, 10);
            if (!isNaN(slideIndex) && slideIndex >= 0 && slideIndex < slides.length) {
                // Use ref to avoid stale closure on activeIndex
                if (slideIndex === activeIndexRef.current) return;
                setIsTransitioning(true);
                setTimeout(() => {
                    setActiveIndex(slideIndex);
                    setIsTransitioning(false);
                }, 500);
            }
        };

        document.addEventListener('rialto-night-slide', handleNightClick);
        return () => document.removeEventListener('rialto-night-slide', handleNightClick);
    }, [slides.length]);

    // Sync the themed nights nav to reflect the active slide
    useEffect(() => {
        if (slides.length === 0) return;
        const currentSlide = slides[activeIndex];
        if (!currentSlide) return;

        const dayName = (currentSlide.day_name || '').toLowerCase();
        document.dispatchEvent(new CustomEvent('rialto-carousel-changed', {
            detail: { dayName, index: activeIndex }
        }));
    }, [activeIndex, slides]);

    const goToSlide = (index) => {
        if (index === activeIndex) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setActiveIndex(index);
            setIsTransitioning(false);
        }, 500);
    };

    if (slides.length === 0) return null;

    const currentSlide = slides[activeIndex];
    const isFeatured = currentSlide?.is_featured;
    const isCustomFeatured = isFeatured && !currentSlide?.is_film_source;
    const film = currentSlide?.film;

    if (!film && !isCustomFeatured) return null;

    const hasTrailer = film?.trailer_embed || film?.trailer_url;
    const backdropUrl = isCustomFeatured
        ? currentSlide.banner_image
        : (film?.backdrop_url || film?.poster);

    const formatDuration = (mins) => {
        if (!mins || mins <= 0) return null;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        if (h > 0 && m > 0) return `${h}h ${m}m`;
        if (h > 0) return `${h}h`;
        return `${m}m`;
    };

    const handleTrailerClick = () => {
        if (isCustomFeatured && currentSlide.video_url) {
            // For custom featured slides, construct a film-like object for the trailer modal
            onTrailerClick && onTrailerClick({
                title: currentSlide.banner_title || '',
                trailer_url: currentSlide.video_url,
                synopsis: currentSlide.description || '',
                url: currentSlide.link_url || '',
                _linkText: currentSlide.link_text || '',
            });
        } else if (onTrailerClick && hasTrailer) {
            onTrailerClick(film);
        }
    };

    // Check if rating is valid (not 0.0 or empty)
    const hasValidRating = film?.rating && parseFloat(film.rating) > 0;

    // Themed night info from the API
    const themedNight = currentSlide.themed_night;
    const themedNightSlug = currentSlide.themed_night_slug;
    const themedNightColor = currentSlide.themed_night_color;

    // The first day slide index (0 if no feature, 1 if feature present)
    const firstDayIndex = hasFeature ? 1 : 0;

    return (
        <section
            className="rialto-hero rialto-hero-carousel"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Background Image with Fade */}
            <div className={`rialto-hero-backdrop ${isTransitioning ? 'transitioning' : ''}`}>
                {backdropUrl && (
                    <img
                        src={backdropUrl}
                        alt=""
                        className="loaded"
                    />
                )}
                <div className="rialto-hero-gradient" />
            </div>

            {/* Content Container (max-width constrained) */}
            <div className="rialto-hero-content-wrapper">
                <div className={`rialto-hero-content ${isTransitioning ? 'transitioning' : ''}`}>

                    {isCustomFeatured ? (
                        <>
                            {/* Custom Featured Banner Content */}
                            {currentSlide.banner_subtitle && (
                                <div className="rialto-hero-tonight-badge">
                                    {currentSlide.banner_subtitle}
                                </div>
                            )}

                            <h1 className="rialto-hero-title">{currentSlide.banner_title}</h1>

                            {currentSlide.description && (
                                <p className="rialto-hero-synopsis">{currentSlide.description}</p>
                            )}

                            <div className="rialto-hero-actions">
                                {currentSlide.video_url && (
                                    <button
                                        className="rialto-btn rialto-btn-primary"
                                        onClick={handleTrailerClick}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <polygon points="5 3 19 12 5 21 5 3" />
                                        </svg>
                                        <span className="btn-label-full">{currentSlide.video_text || 'Watch Trailer'}</span>
                                        <span className="btn-label-short">{currentSlide.video_text || 'Trailer'}</span>
                                    </button>
                                )}
                                {currentSlide.link_url && (
                                    <a href={currentSlide.link_url} className="rialto-btn rialto-btn-secondary">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="16" x2="12" y2="12" />
                                            <line x1="12" y1="8" x2="12.01" y2="8" />
                                        </svg>
                                        <span className="btn-label-full">{currentSlide.link_text || 'More Info'}</span>
                                        <span className="btn-label-short">{currentSlide.link_text || 'Info'}</span>
                                    </a>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Regular Film Slide Content */}
                            {currentSlide.day_short && (
                                <div className="rialto-hero-tonight-badge">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                                        <polyline points="12 6 12 12 16 14" fill="none" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                    {activeIndex === firstDayIndex && currentSlide.date === new Date().toISOString().slice(0, 10) ? 'Tonight' : currentSlide.day_name} at 8:30pm
                                </div>
                            )}

                            {film.pick_of_week && (
                                <div className="rialto-hero-badge">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                    {t.pickOfWeek || 'Pick of the Week'}
                                </div>
                            )}

                            <h1 className="rialto-hero-title">{film.title}</h1>

                            <div className="rialto-hero-meta">
                                {hasValidRating && (
                                    <span className="rialto-hero-rating">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#f5c518">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                        </svg>
                                        {parseFloat(film.rating).toFixed(1)}
                                        {film.votes > 0 && (
                                            <span className="rialto-hero-votes">
                                                ({film.votes.toLocaleString()} votes)
                                            </span>
                                        )}
                                    </span>
                                )}
                                {film.year && <span>{film.year}</span>}
                                {film.duration > 0 && <span>{formatDuration(film.duration)}</span>}
                                {film.certification && (
                                    <span className="rialto-hero-cert">{film.certification}</span>
                                )}
                            </div>

                            {film.genres?.length > 0 && (
                                <div className="rialto-hero-genres">
                                    {film.genres.join(' \u2022 ')}
                                </div>
                            )}

                            {film.synopsis && (
                                <p className="rialto-hero-synopsis">{film.synopsis}</p>
                            )}

                            <div className="rialto-hero-credits">
                                {film.director && (
                                    <p>
                                        <strong>{t.director || 'Director'}:</strong> {film.director}
                                    </p>
                                )}
                                {film.actors && (
                                    <p>
                                        <strong>{t.starring || 'Starring'}:</strong> {film.actors}
                                    </p>
                                )}
                            </div>

                            <div className="rialto-hero-actions">
                                {hasTrailer && (
                                    <button
                                        className="rialto-btn rialto-btn-primary"
                                        onClick={handleTrailerClick}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <polygon points="5 3 19 12 5 21 5 3" />
                                        </svg>
                                        <span className="btn-label-full">{t.watchTrailer || 'Watch Trailer'}</span>
                                        <span className="btn-label-short">Trailer</span>
                                    </button>
                                )}
                                <a href={film.url} className="rialto-btn rialto-btn-secondary">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="16" x2="12" y2="12" />
                                        <line x1="12" y1="8" x2="12.01" y2="8" />
                                    </svg>
                                    <span className="btn-label-full">{t.moreInfo || 'More Info'}</span>
                                    <span className="btn-label-short">Info</span>
                                </a>
                                {themedNight && themedNightSlug && (
                                    <a
                                        href={`/nights/${themedNightSlug}/`}
                                        className="rialto-btn rialto-btn-night"
                                        style={themedNightColor ? { background: `linear-gradient(135deg, ${themedNightColor}, ${themedNightColor}dd)`, borderColor: themedNightColor } : {}}
                                    >
                                        Rialto {themedNight}
                                    </a>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Mobile Day Selector (hidden on desktop where nights-navigation handles it) */}
            {slides.length > 1 && (
                <div className="rialto-hero-day-selector">
                    {slides.map((slide, idx) => (
                        <button
                            key={idx}
                            className={`rialto-hero-day-pill${idx === activeIndex ? ' active' : ''}`}
                            onClick={() => goToSlide(idx)}
                        >
                            {slide.is_featured
                                ? (slide.menu_subtitle || 'FEATURED').toUpperCase()
                                : (idx === firstDayIndex && slide.date === new Date().toISOString().slice(0, 10) ? 'TODAY' : (slide.day_short || '').toUpperCase())
                            }
                        </button>
                    ))}
                </div>
            )}

            {/* Progress Bar */}
            {slides.length > 1 && (
                <div className="rialto-hero-progress">
                    <div
                        className="rialto-hero-progress-bar"
                        style={{
                            animationDuration: isPaused ? '0s' : '5s',
                            animationPlayState: isPaused ? 'paused' : 'running'
                        }}
                        key={activeIndex}
                    />
                </div>
            )}
        </section>
    );
}
